import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getVehiclePartsDepreciation } from "../../services/VehicleService";

const currency = (n) => {
  const num = Number(n || 0);
  return `€ ${num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const SummaryCard = ({ label, value, accent, gradient }) => (
  <div className="col-md-3 col-sm-6 mb-3">
    <div
      style={{
        borderRadius: 14,
        padding: "18px 20px",
        minHeight: 110,
        background: gradient
          ? "linear-gradient(135deg, #6b4ce6 0%, #8e6bff 100%)"
          : "#ffffff",
        color: gradient ? "#ffffff" : "#1f2937",
        boxShadow: gradient
          ? "0 8px 20px rgba(107, 76, 230, 0.25)"
          : "0 2px 8px rgba(0,0,0,0.06)",
        border: gradient ? "none" : "1px solid #eef0f3",
        height: "100%",
      }}
    >
      <div
        style={{
          fontSize: 12,
          fontWeight: 500,
          opacity: gradient ? 0.9 : 0.65,
          textTransform: "uppercase",
          letterSpacing: 0.4,
          marginBottom: 8,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 22,
          fontWeight: 700,
          color: gradient ? "#ffffff" : accent || "#1f2937",
        }}
      >
        {value}
      </div>
    </div>
  </div>
);

const VehicleDepreciation = ({ setActiveTab, vehicleProcessList }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!vehicleProcessList?.vehicle_id) return;
    getVehiclePartsDepreciation({
      vehicle_id: vehicleProcessList?.vehicle_id,
    })
      .then((response) => setData(response.data))
      .catch(() => {});
  }, [vehicleProcessList]);

  const history = useMemo(() => {
    // The API currently returns aggregated totals only.
    // When extended with `history: [{year, initial, amortization, final}]`
    // it will render automatically here.
    if (Array.isArray(data?.history)) return data.history;
    return [];
  }, [data]);

  const initialValue = Number(data?.totalPrice2 || 0);
  const currentValue = Number(data?.totalActPrice1 || 0);
  const years = Number(data?.yearsInService ?? data?.years_in_service ?? 0);
  const pct = Number(
    data?.annualDepreciationPct ?? data?.annual_depreciation_pct ?? 0,
  );

  return (
    <div>
      <div className="row">
        <div className="col-md-12">
          <div
            className="card card-primary"
            style={{ borderRadius: 16, border: "1px solid #eef0f3" }}
          >
            <div className="card-body">
              <div
                className="d-flex align-items-center justify-content-between mb-4"
                style={{ gap: 12 }}
              >
                <div>
                  <h4
                    style={{
                      fontWeight: 700,
                      margin: 0,
                      color: "#1f2937",
                    }}
                  >
                    {t("vehicle_depreciation")}
                  </h4>
                  <small style={{ color: "#6b7280" }}>
                    {t("depreciation_summary")}
                  </small>
                </div>
              </div>

              {/* Summary cards */}
              <div className="row">
                <SummaryCard
                  label={t("initial_value")}
                  value={currency(initialValue)}
                  accent="#0d6efd"
                />
                <SummaryCard
                  label={t("annual_depreciation_pct")}
                  value={`${pct.toFixed(2)} %`}
                  accent="#f97316"
                />
                <SummaryCard
                  label={t("current_value")}
                  value={currency(currentValue)}
                  gradient
                />
              </div>

              {/* Secondary metrics */}
              <div className="row mt-2">
                <SummaryCard
                  label={t("active_parts_value")}
                  value={currency(data?.totalActPrice1)}
                  accent="#16a34a"
                />
                <SummaryCard
                  label={t("inactive_parts_value")}
                  value={currency(data?.totalNotActPrice1)}
                  accent="#dc2626"
                />
                <SummaryCard
                  label={t("total_cost_basis")}
                  value={currency(data?.totalPartCostPrice)}
                  accent="#6366f1"
                />
                <SummaryCard
                  label={t("totalActiveParts")}
                  value={data?.totalActiveParts ?? 0}
                  accent="#0891b2"
                />
              </div>

              {/* History table */}
              <div className="mt-4">
                <h5
                  style={{
                    fontWeight: 600,
                    color: "#1f2937",
                    marginBottom: 12,
                  }}
                >
                  {t("depreciation_history")}
                </h5>
                <div
                  style={{
                    borderRadius: 12,
                    border: "1px solid #eef0f3",
                    overflow: "hidden",
                  }}
                >
                  <table className="table mb-0">
                    <thead>
                      <tr style={{ background: "#f8fafc" }}>
                        <th style={{ fontWeight: 600, color: "#475569" }}>
                          {t("year")}
                        </th>
                        <th style={{ fontWeight: 600, color: "#475569" }}>
                          {t("initial_value")}
                        </th>
                        <th style={{ fontWeight: 600, color: "#475569" }}>
                          {t("annual_amortization")}
                        </th>
                        <th style={{ fontWeight: 600, color: "#475569" }}>
                          {t("final_value")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.length > 0 ? (
                        history.map((row, idx) => (
                          <tr key={idx}>
                            <td>{row.year}</td>
                            <td>{currency(row.initial)}</td>
                            <td>{currency(row.amortization)}</td>
                            <td>{currency(row.final)}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={4}
                            style={{
                              padding: "36px 12px",
                              textAlign: "center",
                              color: "#6b7280",
                            }}
                          >
                            {t("no_depreciation_history")}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Footer: consistent step nav */}
              <div className="back-footer">
                <div className="row align-items-center">
                  <div className="col-4">
                    <button
                      type="button"
                      className="next-button btn btn-secondary"
                      onClick={() => setActiveTab("vehicle_part_check")}
                    >
                      <i className="bi bi-arrow-left"></i> {t("back")}
                    </button>
                  </div>
                  <div className="col-4 text-center">
                    <small style={{ color: "#9ca3af" }}>
                      {t("tool_label")}
                    </small>
                  </div>
                  <div className="col-4 text-end">
                    <button
                      type="button"
                      className="btn btn-primary nxt-btn"
                      onClick={() => setActiveTab("vehicle_part_check_list")}
                    >
                      {t("save_and_continue")}
                      <i className="bi bi-arrow-right ms-1"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleDepreciation;
